/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import { omit, pick } from './lodash';
import Bidi from './Bidi';

function createError(propFullName, componentName) {
	return new Error(
		`Invalid prop '${propFullName}' supplied to '${componentName}'. Validation failed.`
	);
}

export const BaPropTypes = {
	component(component) {
		return function(propValue, key, componentName, location, propFullName) {
			const prop = propValue[key];
			if (prop.type !== component && prop.type.name !== component.name) {
				return createError(propFullName, componentName);
			}

			return undefined;
		};
	},

	range(min, max) {
		return function(propValue, key, componentName, location, propFullName) {
			const value = propValue[key];
			if (value != null && (value < min || value > max)) {
				return createError(propFullName, componentName);
			}

			return undefined;
		};
	},

	notAllowed(notAllowedValues, message) {
		return function(propValue, key, componentName) {
			const value = propValue[key];
			let passed = true;
			for (let i = 0; i < notAllowedValues.length; i++) {
				if (value === notAllowedValues[i]) {
					passed = false;
					break;
				}
			}
			if (value != null && !passed) {
				return new Error(
					`Invalid prop '${key}' supplied to '${componentName}'. ${message || ''}`
				);
			}

			return undefined;
		};
	},

	deprecated(message) {
		return function(propValue, key, componentName, location, propFullName) {
			const value = propValue && propValue[key];
			if (value != null) {
				return new Error(
					`'${key}' supplied to '${componentName}' is deprecated . ${message || ''}`
				);
			}

			return undefined;
		};
	},

	deprecatedByCarbon(Component) {
		BaPropTypes.deprecated(`It is ignored and not applicable to Carbon ${Component.name}`);
	},

	requiredIf(typeCheck, condition) {
		return function(props, propName, componentName, ...other) {
			// if condition is not provided we will assume required is true
			const isRequired =
				typeof condition === 'function' ? condition(props, propName, componentName) : true;

			if (!typeCheck) {
				return new Error(
					`Unable to validate ${propName} for ${componentName}, type check method not provided.`
				);
			}

			const check = isRequired ? typeCheck.isRequired : typeCheck;

			return check(props, propName, componentName, ...other);
		};
	},
};

export function sanitize(props, Component, ...blackList) {
	// Predefined blackList, can be added more if needed
	const defaultBlackList = [
		...(Component ? Object.keys(Component.propTypes) : []),
		...Bidi.getBidiProps(),
	];
	const blackLists = [...blackList, ...defaultBlackList];

	return omit(props, ...blackLists);
}

export function getInheritedProps(props, SuperClass) {
	return pick(props, [...Object.keys(SuperClass.propTypes)]);
}
